import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconAlertTriangle } from "@tabler/icons-react"
import { Link } from "react-router"
import { ORG_REGULATIONS_SETTINGS_ROUTE } from "../../../../api/regulationSettingsApi"
import type { InquiryRegulationOption } from "../../../../api/supplierInquiriesApi"
import { useCanViewOrgRegulations } from "../../../../hooks/useCanViewOrgRegulations"
import { notAskedRegulations } from "../../../../utils/inquiryScope"
import {
  normalizeFrameworkCode,
  regulationLabel,
} from "../../../../utils/regulationLabel"
import { McPill } from "../../../shared/McPill"
import { InquiryNotAskedNote } from "../InquiryNotAskedNote"
import { InquiryRegulationPicker } from "../InquiryRegulationPicker"

/** Makes the note the draft's editable picker (TUR-1730). */
export interface RegulationEditor {
  options: readonly InquiryRegulationOption[]
  onChange: (keys: string[]) => void
  onFollowSettings: () => void
  /** The draft has its own pick (scope_source "explicit"), not Settings. */
  explicit: boolean
  /** Visibly off, e.g. while sending. */
  disabled?: boolean
  /** A regulation change is still saving: the picker waits quietly. */
  busy?: boolean
  /** Bulk: the picker sets every draft of the batch to one selection. */
  bulk?: boolean
  /** Some draft of the batch cannot be sent with its picks (its format asks none). */
  blocked?: boolean
}

interface InquiryRegulationsNoteProps {
  askedRegulations?: string[] | null
  regulationKeys?: string[] | null
  regulationScopeError?: string | null
  /** The inquiry has its own pick (scope_source "explicit"), not Settings. */
  ownPick?: boolean
  editor?: RegulationEditor
}

/**
 * What the questionnaire asks about once the org has picked its regulations in
 * Settings > Regulations: the backend narrows the documents to them and resolves
 * the list per format. With an `editor` (a draft, regulation settings on) it is
 * the picker for this draft's own regulations (TUR-1730); without one it only
 * states them. When the chosen format covers none
 * of them the send is refused, and this explains why instead. Renders nothing
 * while no selection applies with regulation settings off (the documents carry
 * everything, as before); with them on the detail names that full set.
 */
export function InquiryRegulationsNote({
  askedRegulations,
  regulationKeys,
  regulationScopeError,
  ownPick = false,
  editor,
}: InquiryRegulationsNoteProps) {
  const { translate } = useTranslation()
  // The link lands on the Regulations settings page, so only users who see it get it.
  const canOpenRegulations = useCanViewOrgRegulations()
  const settingsLink = canOpenRegulations && (
    <Link
      to={ORG_REGULATIONS_SETTINGS_ROUTE}
      className="text-[12px] font-medium text-yellow1 hover:underline"
    >
      {translate("MC_INQ_DRAFT_REGULATIONS_EDIT")}
    </Link>
  )

  if (editor) {
    const picked = regulationKeys ?? []
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-baseline gap-2">
          {/* The picks, not what the format asks (the note below says what it cannot). */}
          <Typography as="muted" className="text-[11.5px] text-grey5">
            {translate("MC_INQ_DRAFT_REGULATIONS_PICK_LABEL")}
          </Typography>
          {editor.bulk && (
            <Typography as="muted" className="text-[11px] text-grey5 italic">
              {translate("MC_INQ_REG_PICKER_ALL_DRAFTS")}
            </Typography>
          )}
        </div>
        <InquiryRegulationPicker
          options={editor.options}
          value={picked}
          onChange={editor.onChange}
          onFollowSettings={
            editor.explicit ? editor.onFollowSettings : undefined
          }
          disabled={editor.disabled}
          busy={editor.busy}
          pfasNotAsked={
            askedRegulations != null && !askedRegulations.includes("PFAS")
          }
        />
        {regulationScopeError || editor.blocked ? (
          // The pick is saved whatever the format; send refuses it, so say why here.
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2"
          >
            <IconAlertTriangle
              size={14}
              className="mt-px flex-none text-warning"
            />
            <Typography as="muted" className="text-[12px] text-white">
              {translate("MC_INQ_DRAFT_REGULATIONS_NOT_COVERED")}
            </Typography>
          </div>
        ) : (
          <InquiryNotAskedNote
            keys={notAskedRegulations(askedRegulations, picked)}
            askedRegulations={askedRegulations}
          />
        )}
        {!editor.explicit && (
          <div className="flex flex-wrap items-center gap-2">
            <Typography as="muted" className="text-[11.5px] text-grey5">
              {translate("MC_INQ_DRAFT_REGULATIONS_SOURCE")}
            </Typography>
            {settingsLink}
          </div>
        )}
      </div>
    )
  }

  if (regulationScopeError) {
    return (
      <div
        role="alert"
        className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3"
      >
        <IconAlertTriangle size={14} className="mt-px flex-none text-warning" />
        <div className="flex flex-col items-start gap-1">
          <Typography as="muted" className="text-[12px] text-white">
            {translate("MC_INQ_NO_REGULATIONS_ERROR")}
          </Typography>
          {settingsLink}
        </div>
      </div>
    )
  }

  if (!askedRegulations?.length) return null

  // Regulations the inquiry should ask but this format has no section for (e.g. Annex
  // XIV/XVII in the PDF + Excel package): said out loud instead of dropped silently.
  const notAsked = notAskedRegulations(askedRegulations, regulationKeys)

  return (
    <div className="flex flex-col gap-1.5">
      <Typography as="muted" className="text-[11.5px] text-grey5">
        {translate("MC_INQ_DRAFT_REGULATIONS_LABEL")}
      </Typography>
      <div className="flex flex-wrap gap-1.5">
        {askedRegulations.map((key) => (
          <McPill key={key} tone="muted">
            {regulationLabel(normalizeFrameworkCode(key), key, translate)}
          </McPill>
        ))}
      </div>
      <InquiryNotAskedNote
        keys={notAsked}
        askedRegulations={askedRegulations}
      />
      {/* Only a scope that follows Settings is "based on" them. */}
      {!ownPick && (
        <div className="flex flex-wrap items-center gap-2">
          <Typography as="muted" className="text-[11.5px] text-grey5">
            {translate("MC_INQ_DRAFT_REGULATIONS_SOURCE")}
          </Typography>
          {settingsLink}
        </div>
      )}
    </div>
  )
}
